import { assignStyleProperty } from './assignStyle';
import { $classes, $deps } from './constants';
import { createResetStyles } from './createResetStyles';
import { serializeKey, nestSelector } from './serialize';
import { createStyleIntent } from './createStyleIntent';
import { isBatchedStyles, isMedia, isSelector, makeBatchedStyles } from './util';
import { hash } from '../util';
const PROPERTY_FALLBACK = '*';
export function configureCreateStyle(id, properties, plugins, mode) {
    const isOutputBatched = mode === 'batched' || mode === 'global';
    const salt = id + mode;
    return createStyleIntent(mode === 'global' ? 'global' : 'atomic', (input)=>serializeKey(input, salt), (input)=>{
        let processed = false;
        const styles = {};
        if (isOutputBatched) {
            makeBatchedStyles(styles, mode !== 'global');
        }
        return ()=>{
            if (processed) {
                return styles;
            }
            processed = true;
            const activePlugins = {};
            let activeStyleRoot = styles;
            let activeStyles = styles;
            let activeMedia = '';
            let activeSelector = '&';
            function setMediaAndSelector(media, selector, callback) {
                var _activeStyles, _activeSelector;
                const initialMedia = activeMedia;
                const initialStyles = activeStyles;
                const initialSelector = activeSelector;
                activeSelector = selector;
                activeMedia = media;
                if (isBatchedStyles(activeStyleRoot)) {
                    if (activeMedia) {
                        var _activeStyleRoot, _activeMedia;
                        var _;
                        (_ = (_activeStyleRoot = activeStyleRoot)[_activeMedia = activeMedia]) !== null && _ !== void 0 ? _ : _activeStyleRoot[_activeMedia] = {};
                        activeStyles = activeStyleRoot[activeMedia];
                    } else {
                        activeStyles = activeStyleRoot;
                    }
                }
                var _1;
                (_1 = (_activeStyles = activeStyles)[_activeSelector = activeSelector]) !== null && _1 !== void 0 ? _1 : _activeStyles[_activeSelector] = {};
                activeStyles = activeStyles[activeSelector];
                callback();
                activeMedia = initialMedia;
                activeStyles = initialStyles;
                activeSelector = initialSelector;
            }
            const set = {
                addClass (className) {
                    var _styles, _$classes;
                    var _;
                    (_ = (_styles = styles)[_$classes = $classes]) !== null && _ !== void 0 ? _ : _styles[_$classes] = [];
                    const classes = styles[$classes];
                    if ((classes === null || classes === void 0 ? void 0 : classes.indexOf(className)) === -1) {
                        classes.push(className);
                    }
                },
                dependency (intent) {
                    var _styles, _$deps;
                    var _;
                    (_ = (_styles = styles)[_$deps = $deps]) !== null && _ !== void 0 ? _ : _styles[_$deps] = [];
                    const deps = styles[$deps];
                    if ((deps === null || deps === void 0 ? void 0 : deps.indexOf(intent.key)) === -1) {
                        deps.push(intent.key);
                    }
                },
                property (property, value, { skipPlugin = false } = {}) {
                    const propertyName = typeof property === 'string' ? property : property.intent.toString();
                    const isUndefined = typeof value === 'undefined';
                    if (!isUndefined && typeof property !== 'string') {
                        set.dependency(property.intent);
                    }
                    const plugin = !skipPlugin && !activePlugins[propertyName] && plugins[propertyName];
                    const isBatchedStyleRoot = isBatchedStyles(activeStyleRoot);
                    // If `value` is a function, execute it and generate batched styles
                    // to be associated with the property.
                    let processedValue = value;
                    const isFunction = typeof value === 'function';
                    if (isFunction) {
                        processedValue = makeBatchedStyles({});
                        const initialStyleRoot = activeStyleRoot;
                        activeStyleRoot = processedValue;
                        setMediaAndSelector(isBatchedStyleRoot ? '' : activeMedia, activeSelector, value);
                        activeStyleRoot = initialStyleRoot;
                    }
                    if (!isUndefined && !isFunction && plugin) {
                        activePlugins[propertyName] = true;
                        plugin(value, set, propertyName);
                        activePlugins[propertyName] = false;
                    } else if (isBatchedStyleRoot) {
                        activeStyles[propertyName] = processedValue;
                    } else {
                        assignStyleProperty(activeStyles, propertyName, processedValue, activeMedia);
                    }
                },
                media (media, callback) {
                    setMediaAndSelector(media, activeSelector, callback);
                },
                reset (resetStyles) {
                    set.dependency(createResetStyles(resetStyles, activeSelector, activeMedia));
                },
                selector (selector, callback, mode = 'nest') {
                    setMediaAndSelector(activeMedia, mode === 'nest' ? nestSelector(activeSelector, selector) : selector, callback);
                },
                var (property, fallback) {
                    set.dependency(property.intent);
                    const propertyName = property.intent.toString();
                    return typeof fallback === 'undefined' ? `var(${propertyName})` : `var(${propertyName}, ${fallback})`;
                }
            };
            function processInput(input) {
                const keys = Object.keys(input);
                for(let i = 0; i < keys.length; i++){
                    const key = keys[i];
                    const value = input[key];
                    if (isMedia(key)) {
                        // The key is a media query.
                        if (activeSelector !== '&') {
                            throw new Error('Media queries cannot be declared within selector style objects. Place your selector style object within a media query instead.');
                        }
                        if (activeMedia) {
                            throw new Error('Nested media query objects are unsupported');
                        }
                        activeMedia = key;
                        processInput(value);
                        activeMedia = '';
                    } else if (isOutputBatched ? styles === activeStyles : isSelector(key)) {
                        // The key is a selector.
                        if (activeSelector !== '&') {
                            throw new Error('Nested selector objects are unsupported');
                        }
                        const ampersandIndex = key.lastIndexOf('&');
                        const hasAmpersand = ampersandIndex !== -1;
                        if (hasAmpersand) {
                            if (mode === 'global') {
                                throw new Error(`Invalid selector "${key}". Global selectors cannot contain ampersands.`);
                            } else if (ampersandIndex !== key.length - 1) {
                                throw new Error(`Invalid selector "${key}". Atomic selectors with ampersands must have an ampersand as the last character.`);
                            }
                        }
                        let selector = key;
                        if (!isOutputBatched) {
                            if (key.indexOf(',') !== -1) {
                                throw new Error(`Invalid selector "${key}". Atomic selectors cannot contain a list of selectors.`);
                            } else if (key.indexOf('#') !== -1) {
                                throw new Error(`Invalid selector "${key}". Atomic selectors cannot contain id selectors.`);
                            } else if (!hasAmpersand) {
                                selector = `${key}&`;
                            }
                        }
                        set.selector(selector, ()=>{
                            processInput(value);
                        }, 'overwrite');
                    } else {
                        // The key is a property.
                        const property = properties[key] || properties[PROPERTY_FALLBACK];
                        if (property) {
                            const result = property(value, set, key);
                            if (result != null) {
                                set.property(key, result);
                            }
                        } else if (process.env.NODE_ENV !== 'production') {
                            throw new Error(`Missing style property "${key}"`);
                        }
                    }
                }
            }
            processInput(input);
            return styles;
        };
    });
}
/**
 * Creates a custom css utility with support for the provided css properties and plugins.
 *
 * @see https://sail.stripe.me/apis/configurestyle/
 */ export function configureStyle(properties, plugins = {}) {
    const id = hash(Object.keys(properties).toString() + Object.keys(plugins).toString());
    const createStyles = configureCreateStyle(id, properties, plugins, 'atomic');
    createStyles.global = configureCreateStyle(id, properties, plugins, 'global');
    createStyles.properties = properties;
    createStyles.plugins = plugins;
    return createStyles;
}

//# sourceMappingURL=configureStyle.js.map